import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import {
  generateSlugBatch,
  newStoryFromSlug,
  runAutoPipeline,
} from '../pipeline/stages';
import { isSyncConfigured } from '../lib/syncClient';
import { VIBE_LABELS, VIBE_ORDER, type Slug, type Story, type Vibe } from '../types';
import { useWakeLock } from '../lib/useWakeLock';
import './routes.css';

function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

type SlugGroup = { batchId: string; slugs: Slug[]; createdAt: number };

function groupByBatch(slugs: Slug[]): SlugGroup[] {
  const map = new Map<string, SlugGroup>();
  for (const s of slugs) {
    const g = map.get(s.batchId);
    if (g) {
      g.slugs.push(s);
      if (s.createdAt < g.createdAt) g.createdAt = s.createdAt;
    } else {
      map.set(s.batchId, { batchId: s.batchId, slugs: [s], createdAt: s.createdAt });
    }
  }
  return [...map.values()].sort((a, b) => b.createdAt - a.createdAt);
}

export default function SlugsRoute() {
  const activeUniverseId = useAppStore((s) => s.ui.activeUniverseId);
  const universes = useAppStore((s) => s.universes);
  const slugPool = useAppStore((s) => s.slugPool);
  const stories = useAppStore((s) => s.stories);
  const settings = useAppStore((s) => s.settings);
  const addSlugs = useAppStore((s) => s.addSlugs);
  const hideSlug = useAppStore((s) => s.hideSlug);
  const createStory = useAppStore((s) => s.createStory);
  const setActiveStoryForUniverse = useAppStore((s) => s.setActiveStoryForUniverse);
  const setBatchVibe = useAppStore((s) => s.setBatchVibe);
  const setBatchGuidance = useAppStore((s) => s.setBatchGuidance);
  const nav = useNavigate();

  const universe = activeUniverseId ? universes[activeUniverseId] : undefined;

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [protagonists, setProtagonists] = useState(universe?.defaultProtagonists ?? '');
  const [errorBySlug, setErrorBySlug] = useState<Record<string, string>>({});

  const storyBySlug = useMemo(() => {
    const m = new Map<string, Story>();
    for (const s of Object.values(stories)) m.set(s.slug.id, s);
    return m;
  }, [stories]);

  const autoActive = useMemo(
    () =>
      Object.values(stories).some(
        (s) => s.universeId === activeUniverseId && s.inFlight !== undefined
      ),
    [stories, activeUniverseId]
  );
  useWakeLock(busy || autoActive, 'slugs');
  const [justAddedBatch, setJustAddedBatch] = useState<string | undefined>();
  const [nowTick, setNowTick] = useState(0);

  // Reset protagonists field when switching universes.
  useEffect(() => {
    setProtagonists(universe?.defaultProtagonists ?? '');
  }, [activeUniverseId, universe?.defaultProtagonists]);

  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!justAddedBatch) return;
    const t = setTimeout(() => setJustAddedBatch(undefined), 4000);
    return () => clearTimeout(t);
  }, [justAddedBatch]);

  if (!universe) {
    return (
      <div className="route">
        <h1>Story Ideas</h1>
        <div className="dim">No active universe. Pick one from the home screen.</div>
        <button onClick={() => nav('/')}>Go to universes</button>
      </div>
    );
  }

  const vibe: Vibe = universe.batchVibe ?? 'sleepy';
  const guidance = universe.batchGuidance ?? '';
  const canGenerate = isSyncConfigured();
  const hasVoice = !!universe.defaultVoiceId;
  const canAuto = canGenerate && hasVoice;
  const visible = slugPool.filter(
    (s) => !s.hidden && s.universeId === universe.id
  );
  const groups = useMemo(() => groupByBatch(visible), [visible]);
  void nowTick;

  async function onGenerate() {
    if (!universe) return;
    setErr(undefined);
    setBusy(true);
    try {
      const seenTitles = slugPool
        .filter((s) => s.universeId === universe.id)
        .map((s) => `${s.title} — ${s.hook}`);
      const slugs = await generateSlugBatch({
        settings,
        universeId: universe.id,
        protagonists,
        vibe,
        guidance,
        seenTitles,
      });
      if (slugs.length === 0) throw new Error('No slugs parsed from model output');
      addSlugs(slugs);
      setJustAddedBatch(slugs[0].batchId);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function onPick(slugId: string) {
    if (!universe) return;
    const slug = slugPool.find((s) => s.id === slugId);
    if (!slug) return;
    const existing = storyBySlug.get(slugId);
    const story = existing ?? newStoryFromSlug(slug);
    if (!existing) createStory(story);
    setActiveStoryForUniverse(universe.id, story.id);
    hideSlug(slug.id);
    nav('/workspace');
  }

  async function onAuto(slugId: string) {
    if (!universe) return;
    const slug = slugPool.find((s) => s.id === slugId);
    if (!slug) return;
    const existing = storyBySlug.get(slugId);
    const story = existing ?? newStoryFromSlug(slug);
    if (!existing) createStory(story);
    setErrorBySlug((m) => {
      const { [slugId]: _drop, ...rest } = m;
      return rest;
    });
    try {
      await runAutoPipeline({ story, settings });
      hideSlug(slug.id);
    } catch (e) {
      setErrorBySlug((m) => ({ ...m, [slugId]: (e as Error).message }));
    }
  }

  return (
    <div className="route">
      <h1>Story Ideas</h1>

      <div className="card">
        <label>
          <div className="label">Who</div>
          <input
            value={protagonists}
            onChange={(e) => setProtagonists(e.target.value)}
            placeholder="characters"
          />
        </label>
        <div>
          <div className="label">Mood</div>
          <div className="vibe-row">
            {VIBE_ORDER.map((v) => (
              <button
                key={v}
                type="button"
                className={`vibe-pill ${vibe === v ? 'active' : ''}`}
                onClick={() => setBatchVibe(universe.id, v)}
              >
                {VIBE_LABELS[v]}
              </button>
            ))}
          </div>
        </div>
        <label>
          <div className="label">Guidance for this batch (optional)</div>
          <textarea
            rows={3}
            value={guidance}
            onChange={(e) => setBatchGuidance(universe.id, e.target.value)}
            placeholder="anything specific you want this batch to lean into — a setting, a season, a feeling, a constraint…"
          />
        </label>
        <button
          className="primary"
          onClick={onGenerate}
          disabled={busy || !canGenerate}
        >
          {busy ? 'Generating…' : 'Generate a batch'}
        </button>
        {!canGenerate && (
          <div className="warn">Configure sync in Settings to generate.</div>
        )}
        {err && <div className="error">{err}</div>}
      </div>

      <div className="slug-list">
        {visible.length === 0 && (
          <div className="dim">
            No slugs yet — generate a batch above.
          </div>
        )}
        {groups.map((group, gi) => {
          const isNewest = gi === 0;
          const isJustAdded = group.batchId === justAddedBatch;
          const fromChat = group.slugs.every((s) => s.source === 'chat');
          return (
            <div key={group.batchId} className="batch-group">
              <div className="batch-header">
                <span className="batch-label">
                  {isNewest ? 'Newest batch' : 'Earlier batch'}
                </span>
                {fromChat && (
                  <span className="tag tag-soft batch-source">from chat</span>
                )}
                <span className="batch-time">{formatRelative(group.createdAt)}</span>
                {isJustAdded && <span className="batch-fresh">new</span>}
              </div>
              {group.slugs.map((slug) => {
                const persisted = storyBySlug.get(slug.id);
                const inFlight = persisted?.inFlight;
                const running = inFlight !== undefined;
                const error = errorBySlug[slug.id];
                const persistedProgress = persisted
                  ? persisted.audio
                    ? 'audio ready'
                    : persisted.proseKey
                      ? 'outline & prose saved'
                      : persisted.outlineText
                        ? 'outline saved'
                        : undefined
                  : undefined;
                return (
                  <div
                    key={slug.id}
                    className={`slug-card ${isJustAdded ? 'slug-card-fresh' : ''}`}
                  >
                    <div className="slug-title">
                      {slug.title}
                      {slug.vibe && (
                        <span className="tag tag-soft vibe-tag">{VIBE_LABELS[slug.vibe]}</span>
                      )}
                    </div>
                    <div className="slug-hook">{slug.hook}</div>
                    {running && (
                      <div className="auto-status">
                        <span className="auto-dot" />
                        {inFlight === 'outline' && 'Outlining…'}
                        {inFlight === 'prose' && 'Writing…'}
                        {inFlight === 'tts' && 'Recording…'}
                      </div>
                    )}
                    {!running && error && (
                      <div className="error small">Auto-build failed: {error}</div>
                    )}
                    {!running && !error && persistedProgress && (
                      <div className="dim small">{persistedProgress} — will resume</div>
                    )}
                    <div className="slug-actions">
                      {!running && (
                        <button
                          className="primary"
                          onClick={() => onAuto(slug.id)}
                          disabled={!canAuto}
                          title={
                            !canAuto
                              ? 'Set Anthropic + ElevenLabs keys (Settings) and a default voice (Universe settings)'
                              : undefined
                          }
                        >
                          {error
                            ? 'Retry auto'
                            : persistedProgress
                              ? 'Resume auto'
                              : 'Auto'}
                        </button>
                      )}
                      {!running && (
                        <button onClick={() => onPick(slug.id)}>Edit</button>
                      )}
                      {!running && (
                        <button className="ghost" onClick={() => hideSlug(slug.id)}>
                          Hide
                        </button>
                      )}
                      {!running && error && persisted && (
                        <button
                          className="ghost"
                          onClick={() => {
                            setActiveStoryForUniverse(universe.id, persisted.id);
                            nav('/workspace');
                          }}
                        >
                          Open in workspace
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
